import React from 'react';
import {
    Box,
    Container,
    Typography,
    Stack,
    Chip,
    Button,
    Grid,
    Paper,
    Card,
    CardContent,
    Divider,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
} from '@mui/material';
import PeopleIcon from '@mui/icons-material/People';
import EventIcon from '@mui/icons-material/Event';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import LanguageIcon from '@mui/icons-material/Language';
import BusinessIcon from '@mui/icons-material/Business';
import OndemandVideoIcon from '@mui/icons-material/OndemandVideo';
import DescriptionIcon from '@mui/icons-material/Description';
import GroupsIcon from '@mui/icons-material/Groups';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import Footer from './Footer';

const NAVY = '#003E7E';
const ORANGE = '#FF6600';
const CONTACT_EMAIL = 'trevor@sdsconsortium.com';
const MAILTO = `mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent('Platform Sponsorship Inquiry')}`;

// ── Audience at-a-glance ────────────────────────────────────────────────
const STATS = [
    { icon: <PeopleIcon />, value: '2,500', label: 'Members — substation design engineers & specifiers' },
    { icon: <EventIcon />, value: '~400', label: 'Attendees at our annual U.S. conference' },
    { icon: <CalendarMonthIcon />, value: '365', label: 'Days a year your brand is in front of them' },
];

// ── Where a sponsor appears (mirrors the platform placement model) ──────
const SURFACES = [
    { icon: <LanguageIcon />, title: 'Site-wide Platform Partners strip', desc: 'Your logo on every page of the member platform.' },
    { icon: <BusinessIcon />, title: 'Sponsor directory page', desc: 'Your logo, a short description, and a link to your site.' },
    { icon: <OndemandVideoIcon />, title: 'Recordings hub & player', desc: '"Presented by" alongside our year-round library of session and committee recordings.' },
    { icon: <DescriptionIcon />, title: 'Content & resources library', desc: 'A supporter slot on the documents and standards our members download.' },
    { icon: <GroupsIcon />, title: 'Monthly committee calls', desc: '"Supported by" on the recurring meeting pages our most active members visit every month.' },
];

// ── Sponsorship tiers ───────────────────────────────────────────────────
const TIERS = [
    {
        name: 'Platform Partner',
        price: '$10,000',
        cadence: '/ year',
        featured: true,
        note: 'Limited to 3–4 partners',
        includes: [
            'Presence on every surface — site-wide strip, directory, recordings, content library, and monthly-call pages',
            'Top placement and largest logo',
            'Recognition at the annual conference',
        ],
    },
    {
        name: 'Section Sponsor',
        price: '$4,500',
        cadence: '/ year',
        featured: false,
        note: null,
        includes: [
            'Sponsor directory listing',
            'Plus one year-round section of your choice (e.g. the recordings hub or a committee’s content)',
            'Recognition at the annual conference',
        ],
    },
    {
        name: 'Supporting Sponsor',
        price: '$1,500',
        cadence: '/ year',
        featured: false,
        note: null,
        includes: [
            'Recognition on the sponsor directory page with logo and link',
            'The simplest way to show the community you stand behind it',
        ],
    },
];

const ctaSx = {
    backgroundColor: ORANGE,
    color: 'white',
    fontWeight: 600,
    textTransform: 'none',
    borderRadius: 2,
    boxShadow: '0 6px 14px rgba(255, 102, 0, 0.3)',
    '&:hover': {
        backgroundColor: '#e55a00',
        transform: 'translateY(-2px)',
        boxShadow: '0 10px 18px rgba(255, 102, 0, 0.4)',
    },
    transition: 'all 0.3s ease',
};

export default function SponsorOpportunity() {
    return (
        <Box sx={{ backgroundColor: '#FAFAFA' }}>
            {/* ── Hero ─────────────────────────────────────────────── */}
            <Box
                sx={{
                    background: `linear-gradient(135deg, ${NAVY} 0%, #0055A5 60%, ${NAVY} 100%)`,
                    color: 'white',
                    py: { xs: 5, md: 8 },
                    position: 'relative',
                    overflow: 'hidden',
                }}
            >
                <Box sx={{
                    position: 'absolute', inset: 0,
                    background:
                        'radial-gradient(800px circle at 20% 30%, rgba(25,118,210,0.25), transparent 50%),' +
                        'radial-gradient(600px circle at 80% 20%, rgba(255,102,0,0.12), transparent 45%)',
                    pointerEvents: 'none',
                }} />
                <Container maxWidth="md" sx={{ position: 'relative' }}>
                    <Stack spacing={2.5} alignItems="center" textAlign="center">
                        <Chip
                            label="Year-Round Platform Sponsorship"
                            sx={{ backgroundColor: ORANGE, color: 'white', fontWeight: 600 }}
                        />
                        <Typography
                            variant="h3"
                            sx={{
                                fontWeight: 800,
                                letterSpacing: -0.5,
                                fontSize: { xs: '1.9rem', sm: '2.4rem', md: '3rem' },
                            }}
                        >
                            Become an SDSIC Platform Sponsor
                        </Typography>
                        <Typography variant="h6" sx={{ opacity: 0.92, fontWeight: 400, maxWidth: 720 }}>
                            Reach 2,500 substation design specifiers — all year, not just at the conference.
                        </Typography>
                        <Button
                            variant="contained"
                            href={MAILTO}
                            size="large"
                            startIcon={<MailOutlineIcon />}
                            sx={{ ...ctaSx, fontSize: '1.05rem', py: 1.4, px: 4 }}
                        >
                            Start a Conversation
                        </Button>
                    </Stack>
                </Container>
            </Box>

            {/* ── Audience stats ───────────────────────────────────── */}
            <Container maxWidth="lg" sx={{ py: { xs: 5, md: 7 } }}>
                <Grid container spacing={3}>
                    {STATS.map((s) => (
                        <Grid item xs={12} sm={4} key={s.label}>
                            <Paper
                                elevation={2}
                                sx={{ p: 3, borderRadius: 3, textAlign: 'center', height: '100%' }}
                            >
                                <Box sx={{ color: ORANGE, '& svg': { fontSize: 40 }, mb: 1 }}>{s.icon}</Box>
                                <Typography variant="h3" sx={{ fontWeight: 800, color: NAVY, lineHeight: 1 }}>
                                    {s.value}
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                    {s.label}
                                </Typography>
                            </Paper>
                        </Grid>
                    ))}
                </Grid>
            </Container>

            {/* ── Why it's different ───────────────────────────────── */}
            <Box sx={{ backgroundColor: '#fff', py: { xs: 5, md: 7 } }}>
                <Container maxWidth="md">
                    <Stack spacing={2.5} textAlign="center" alignItems="center">
                        <Typography variant="h4" sx={{ fontWeight: 700, color: NAVY }}>
                            A precise audience — not a banner you forget by Friday
                        </Typography>
                        <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 760, lineHeight: 1.8 }}>
                            SDSIC members are the exact people who specify and buy switchgear, relays, design
                            software, and engineering services. Most sponsorship in our industry is
                            conference-only — three days, then it's gone. Platform sponsorship is different:
                            your presence lives in the website our members use all year, on the pages where
                            they watch recordings, download standards, and join their monthly committee calls.
                        </Typography>
                    </Stack>
                </Container>
            </Box>

            {/* ── Where your brand appears ─────────────────────────── */}
            <Container maxWidth="lg" sx={{ py: { xs: 5, md: 7 } }}>
                <Typography variant="h4" sx={{ fontWeight: 700, color: NAVY, textAlign: 'center', mb: 1 }}>
                    Where your brand appears
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ textAlign: 'center', mb: 4 }}>
                    Curated and tasteful — "supported by," never an ad. The member experience always comes first.
                </Typography>
                <Grid container spacing={3}>
                    {SURFACES.map((p) => (
                        <Grid item xs={12} md={6} key={p.title}>
                            <Paper elevation={1} sx={{ p: 3, borderRadius: 3, display: 'flex', gap: 2, height: '100%' }}>
                                <Box sx={{ color: ORANGE, '& svg': { fontSize: 32 }, mt: 0.5 }}>{p.icon}</Box>
                                <Box>
                                    <Typography variant="h6" sx={{ fontWeight: 700, color: NAVY }}>
                                        {p.title}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                        {p.desc}
                                    </Typography>
                                </Box>
                            </Paper>
                        </Grid>
                    ))}
                </Grid>
            </Container>

            {/* ── Tiers ────────────────────────────────────────────── */}
            <Box sx={{ backgroundColor: '#fff', py: { xs: 5, md: 7 } }}>
                <Container maxWidth="lg">
                    <Typography variant="h4" sx={{ fontWeight: 700, color: NAVY, textAlign: 'center', mb: 1 }}>
                        Sponsorship tiers
                    </Typography>
                    <Typography variant="body1" color="text.secondary" sx={{ textAlign: 'center', mb: 4 }}>
                        Choose the level that fits your goals. All tiers directly sustain the platform 2,500 of your customers rely on.
                    </Typography>
                    <Grid container spacing={3} alignItems="stretch">
                        {TIERS.map((t) => (
                            <Grid item xs={12} md={4} key={t.name}>
                                <Card
                                    elevation={t.featured ? 8 : 2}
                                    sx={{
                                        height: '100%',
                                        borderRadius: 3,
                                        position: 'relative',
                                        border: t.featured ? `2px solid ${ORANGE}` : '1px solid #e0e0e0',
                                        display: 'flex',
                                        flexDirection: 'column',
                                    }}
                                >
                                    {t.featured && (
                                        <Chip
                                            icon={<WorkspacePremiumIcon sx={{ color: 'white !important' }} />}
                                            label="Most Visibility"
                                            sx={{
                                                position: 'absolute', top: -14, left: '50%', transform: 'translateX(-50%)',
                                                backgroundColor: ORANGE, color: 'white', fontWeight: 600,
                                            }}
                                        />
                                    )}
                                    <CardContent sx={{ p: 3, display: 'flex', flexDirection: 'column', height: '100%' }}>
                                        <Typography variant="h6" sx={{ fontWeight: 700, color: NAVY, mt: t.featured ? 1 : 0 }}>
                                            {t.name}
                                        </Typography>
                                        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5, mt: 1 }}>
                                            <Typography variant="h4" sx={{ fontWeight: 800, color: NAVY }}>
                                                {t.price}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">{t.cadence}</Typography>
                                        </Box>
                                        {t.note && (
                                            <Typography variant="caption" sx={{ color: ORANGE, fontWeight: 600 }}>
                                                {t.note}
                                            </Typography>
                                        )}
                                        <Divider sx={{ my: 2 }} />
                                        <List dense disablePadding sx={{ flexGrow: 1 }}>
                                            {t.includes.map((item, i) => (
                                                <ListItem key={i} disableGutters alignItems="flex-start" sx={{ py: 0.5 }}>
                                                    <ListItemIcon sx={{ minWidth: 32, mt: 0.3 }}>
                                                        <CheckCircleIcon sx={{ fontSize: 20, color: ORANGE }} />
                                                    </ListItemIcon>
                                                    <ListItemText
                                                        primary={item}
                                                        primaryTypographyProps={{ variant: 'body2' }}
                                                    />
                                                </ListItem>
                                            ))}
                                        </List>
                                        <Button
                                            fullWidth
                                            variant={t.featured ? 'contained' : 'outlined'}
                                            href={MAILTO}
                                            sx={t.featured
                                                ? { ...ctaSx, mt: 2 }
                                                : { mt: 2, color: NAVY, borderColor: NAVY, textTransform: 'none', fontWeight: 600, borderRadius: 2 }}
                                        >
                                            Inquire
                                        </Button>
                                    </CardContent>
                                </Card>
                            </Grid>
                        ))}
                    </Grid>

                    {/* Founding offer */}
                    <Paper
                        elevation={0}
                        sx={{
                            mt: 4, p: 3, borderRadius: 3, textAlign: 'center',
                            backgroundColor: 'rgba(255, 102, 0, 0.08)',
                            border: `1px dashed ${ORANGE}`,
                        }}
                    >
                        <Typography variant="h6" sx={{ fontWeight: 700, color: NAVY }}>
                            Founding Sponsor offer
                        </Typography>
                        <Typography variant="body1" color="text.secondary" sx={{ mt: 0.5 }}>
                            Be one of our first Platform Partners and lock in <strong>30% off for a two-year term</strong>
                            {' '}— Platform Partner at <strong>$7,000/yr</strong>. Limited to the founding cohort.
                        </Typography>
                    </Paper>
                </Container>
            </Box>

            {/* ── Final CTA ────────────────────────────────────────── */}
            <Box sx={{ py: { xs: 6, md: 8 }, textAlign: 'center' }}>
                <Container maxWidth="sm">
                    <Typography variant="h4" sx={{ fontWeight: 700, color: NAVY, mb: 1.5 }}>
                        Let's talk
                    </Typography>
                    <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                        We'd welcome a short conversation about which tier fits your goals.
                    </Typography>
                    <Button
                        variant="contained"
                        href={MAILTO}
                        size="large"
                        startIcon={<MailOutlineIcon />}
                        sx={{ ...ctaSx, fontSize: '1.1rem', py: 1.5, px: 4 }}
                    >
                        {CONTACT_EMAIL}
                    </Button>
                    <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 2 }}>
                        SDSIC is organizing as a Florida 501(c)(6) industry association. Sponsorship supports the year-round member platform.
                    </Typography>
                </Container>
            </Box>

            <Footer />
        </Box>
    );
}
